import React, { useState, useEffect, useRef } from 'react';
import api from '../config/axios';
import { useUpdateSEO } from '../hooks/useSEO';
import Footer from '../components/footer';
import Menu from '../components/menu';

interface User {
  userId: number;
  prenom: string;
  nom: string;
  name: string;
}

interface Message {
  messageId: number;
  senderId: number;
  receiverId: number;
  content: string;
  isRead: boolean;
  sentAt: string;
}

const Messages: React.FC = () => {
  // Mettre à jour le SEO pour la page messages
  useUpdateSEO({
    title: 'Messages - TournoisPadel',
    description: 'Communiquez avec d\'autres joueurs de padel et organisateurs de tournois.',
  });

  const [contacts, setContacts] = useState<User[]>([]);
  const [selectedContact, setSelectedContact] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [userId, setUserId] = useState<number | null>(null);
  const [MessagesNonlus, setMessagesNonlus] = useState<{ [key: number]: number }>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isContactsVisible, setIsContactsVisible] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await api.get('/messages/contacts');
        setContacts(response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des contacts:', error);
      }
    };

    const fetchUserId = async () => {
      try {
        const response = await api.get('users/me');
        setUserId(response.data.userId);
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'ID utilisateur:', error);
      }
    };

    fetchContacts();
    fetchUserId();
  }, []);

  useEffect(() => {
    const fetchUnreadMessages = async () => {
        try {
            const response = await api.get('/messages/unread-by-contact');
            setMessagesNonlus(response.data);
        } catch (error) {
            console.error('Error fetching unread messages:', error);
        }
    };

    fetchUnreadMessages();
}, []);

  const handleContactClick = (contact: User) => {
    
    setSelectedContact(contact);
    
    setIsContactsVisible(false);
    // Fetch messages for the selected contact
    const fetchMessages = async () => {
      try {
        const response = await api.get(`/messages/contacts/${contact.userId}`);
        setMessages(response.data);

        // Marquer les messages non lus comme lus
        const unreadMessages = response.data.filter((msg: Message) => !msg.isRead);
        unreadMessages.forEach(async (msg: Message) => {
            try {
                await api.patch(`/messages/${msg.messageId}/read`);
            } catch (error) {
                console.error('Erreur lors du marquage comme lu:', error);
            }
        });

    // Remove unread count for this contact
        setMessagesNonlus(prev => {
          const updated = { ...prev };
          delete updated[contact.userId];
          return updated;
      });

        scrollToBottom();
      } catch (error) {
        console.error('Erreur lors de la récupération des messages:', error);
      }
    };
    fetchMessages();
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await api.post('/messages', {
        receiverId: selectedContact!.userId,
        content: newMessage,
      });

      setMessages([
        ...messages,
        {
          messageId: Date.now(),
          senderId: userId!, // ID de l'utilisateur connecté, à récupérer dynamiquement
          receiverId: selectedContact!.userId,
          content: newMessage,
          isRead: false,
          sentAt: new Date().toISOString(),
        },
      ]);

      setNewMessage('');
      
      localStorage.setItem('selectedContact', JSON.stringify(selectedContact)); // Store selected contact
      scrollToBottom();

    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    }
  };

  const scrollToBottom = () => {
    messagesContainerRef.current?.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };


  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    status: string;
    subscriptionValidUntil: string | null;
}>({ status: '', subscriptionValidUntil: null });
    useEffect(() => {

    const fetchSubscriptionStatus = async () => {
    
    const response = await api.get('/account/subscription-status');
                
    const data = response.data;

    setSubscriptionStatus(data);
    };

    fetchSubscriptionStatus();
}, []);


  return (
    <div className="flex flex-col h-screen">
      <Menu />
      
      <div className="flex-1 container mx-auto p-4">
        <div className="flex h-[calc(100vh-12rem)] bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Liste des contacts */}
          <div className={`md:flex md:w-1/3 lg:w-1/4 bg-gray-50 border-r ${isContactsVisible ? 'block' : 'hidden'} md:block`}>
            <div className="w-full">
              <div className="p-4 border-b">
                <h2 className="text-xl font-semibold text-gray-800">Messages</h2>
              </div>
              <div className="overflow-y-auto h-[calc(100vh-10rem)]">
                {contacts.map((contact) => (
                  <div
                    key={contact.userId}
                    onClick={() => handleContactClick(contact)}
                    className={`p-4 flex items-center space-x-3 hover:bg-gray-100 cursor-pointer transition-colors
                      ${selectedContact?.userId === contact.userId ? 'bg-orange-50 border-l-4 border-orange-500' : ''}`}
                  >
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium">{contact.prenom[0]}{contact.nom[0]}</span>
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                      <h3 className="font-medium">{contact.prenom} {contact.nom}</h3>
                      {MessagesNonlus[contact.userId] > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          {MessagesNonlus[contact.userId]}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Zone des messages */}
          <div className={`flex flex-col w-full md:w-2/3 lg:w-3/4 bg-white p-4 overflow-y-auto ${isContactsVisible ? 'hidden' : 'block'} md:block`} ref={messagesContainerRef}>
            <div className="w-full md:hidden items-end">
            <button
              onClick={() => setIsContactsVisible(true)}
              className="btn bg-orange-500 hover:bg-orange-600 "
            >
              Afficher les contacts
            </button>
            </div>
            {selectedContact ? (
              <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold mb-4">Messages avec {selectedContact.prenom} {selectedContact.nom}</h2>                
              </div>
                <div className="flex-1 overflow-y-auto mb-4">
                  {messages.map(msg => (
                    <div
                    key={msg.messageId}
                    className={`flex ${msg.senderId === userId ? 'justify-end' : 'justify-start'}`}
                >
                    <div className={`${msg.senderId === userId ? 'order-1' : 'order-2'}`}>
                        <div className={`chat ${msg.senderId === userId ? 'chat-end' : 'chat-start'}`}>
                            <div className="chat-header opacity-50 text-xs mb-1">
                                {new Date(msg.sentAt).toLocaleString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                            </div>
                            <div className={`max-w-[100%] chat-bubble ${
                                msg.senderId === userId ? 'bg-blue-600' : 'bg-gray-200 text-black'
                            } shadow-sm`}>
                                {msg.content}
                            </div>
                        </div>
                    </div>
                </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div className="flex">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1 border rounded p-2 mr-2"
                    placeholder="Écrire un message..."
                  />
                  <button
                    onClick={handleSendMessage}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Envoyer
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <p className="text-gray-500">Sélectionnez un contact pour démarrer une conversation</p>
              </div>
            )}
          </div>
        </div>
      </div>      
      <Footer />
    </div>
  );
};

export default Messages;